import { useEffect, useState } from "react";
import { api, useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Building2, Plus, Pause, Play, UserCog } from "lucide-react";

export default function Organizations() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    display_name: "",
    contact_email: "",
    address: "",
    admin_name: "",
    admin_email: "",
    admin_password: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/organizations");
      setOrgs(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (user?.role !== "global_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const resetForm = () => setForm({
    name: "", display_name: "", contact_email: "", address: "",
    admin_name: "", admin_email: "", admin_password: "",
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/organizations", form);
      toast.success(`Created ${form.display_name}`);
      setCreating(false);
      resetForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (org) => {
    const newStatus = org.status === "active" ? "suspended" : "active";
    if (!window.confirm(
      newStatus === "suspended"
        ? `Suspend ${org.display_name}? All their users will be blocked from logging in.`
        : `Reactivate ${org.display_name}?`
    )) return;
    try {
      await api.put(`/admin/organizations/${org.id}`, { status: newStatus });
      toast.success(`${org.display_name} is now ${newStatus}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const handleImpersonate = async (org) => {
    if (!window.confirm(`Impersonate the admin of ${org.display_name}? You'll be logged in as their admin for 1 hour. Your action is audit-logged.`)) return;
    try {
      const { data } = await api.post(`/admin/organizations/${org.id}/impersonate`);
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      toast.success(`Impersonating ${data.user?.name || "org admin"}`);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impersonation failed");
    }
  };

  if (loading) {
    return <div className="text-[#7A8A84]" data-testid="orgs-loading">Loading organizations…</div>;
  }

  return (
    <div className="max-w-6xl" data-testid="organizations-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#1B4332]/10 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-[#1B4332]" />
            </div>
            <h1 className="text-2xl font-semibold text-[#1C1F1E]" style={{ fontFamily: "Manrope, sans-serif" }}>
              Organizations
            </h1>
          </div>
          <p className="text-sm text-[#7A8A84] max-w-2xl">
            Manage hospitals using the Mission Visitor Approval platform. Each organization has fully isolated users, requests, and data.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} data-testid="new-org-btn" className="bg-[#1B4332] hover:bg-[#143126]">
          <Plus className="h-4 w-4 mr-2" /> New Organization
        </Button>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader className="pb-0">
          <CardTitle className="text-sm text-[#7A8A84] font-medium">
            {orgs.length} organization{orgs.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[#7A8A84] border-b border-[#E8E8E6]">
                  <th className="pb-3 pr-4">Hospital</th>
                  <th className="pb-3 pr-4">Contact</th>
                  <th className="pb-3 pr-4 text-right">Users</th>
                  <th className="pb-3 pr-4 text-right">Requests</th>
                  <th className="pb-3 pr-4">Status</th>
                  <th className="pb-3 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} className="border-b border-[#F4F4F1] last:border-0" data-testid={`org-row-${o.name}`}>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-[#1C1F1E]">{o.display_name}</div>
                      <div className="text-xs text-[#7A8A84]">slug: {o.name}</div>
                    </td>
                    <td className="py-3 pr-4 text-[#525C58]">{o.contact_email}</td>
                    <td className="py-3 pr-4 text-right font-mono">{o.user_count}</td>
                    <td className="py-3 pr-4 text-right font-mono">{o.request_count}</td>
                    <td className="py-3 pr-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        o.status === "active"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-red-100 text-red-800"
                      }`}>
                        {o.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleImpersonate(o)}
                          data-testid={`impersonate-${o.name}`}
                          className="p-1.5 rounded hover:bg-[#F4F4F1] text-[#525C58] hover:text-[#1B4332] transition-colors"
                          title="Log in as this org's admin"
                        >
                          <UserCog className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(o)}
                          data-testid={`toggle-status-${o.name}`}
                          className="p-1.5 rounded hover:bg-[#F4F4F1] text-[#525C58] hover:text-[#1B4332] transition-colors"
                          title={o.status === "active" ? "Suspend" : "Reactivate"}
                        >
                          {o.status === "active" ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {orgs.length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-12 text-center text-[#7A8A84]">
                      No organizations yet. Click "New Organization" to add the first one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={(o) => { setCreating(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-lg" data-testid="new-org-dialog">
          <DialogHeader>
            <DialogTitle>Create new organization</DialogTitle>
            <DialogDescription>
              Sets up a fresh tenant with its first administrator account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="org_name" className="text-xs">Slug (lowercase, no spaces)</Label>
                <Input id="org_name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                  placeholder="tenwek" data-testid="org-slug-input" />
              </div>
              <div>
                <Label htmlFor="org_display" className="text-xs">Display name</Label>
                <Input id="org_display" required value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  placeholder="Tenwek Mission Hospital" data-testid="org-display-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="org_email" className="text-xs">Contact email</Label>
              <Input id="org_email" type="email" required value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                placeholder="hospitality@tenwek.org" data-testid="org-contact-input" />
            </div>
            <div>
              <Label htmlFor="org_addr" className="text-xs">Address (optional)</Label>
              <Textarea id="org_addr" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2} placeholder="P.O. Box 39, Bomet, Kenya" data-testid="org-addr-input" />
            </div>
            <div className="pt-2 border-t border-[#E8E8E6]">
              <p className="text-xs font-medium text-[#7A8A84] uppercase tracking-wide mb-3">
                First Admin User
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="admin_name" className="text-xs">Name</Label>
                  <Input id="admin_name" required value={form.admin_name}
                    onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                    placeholder="Tenwek Admin" data-testid="admin-name-input" />
                </div>
                <div>
                  <Label htmlFor="admin_email" className="text-xs">Admin email</Label>
                  <Input id="admin_email" type="email" required value={form.admin_email}
                    onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                    placeholder="admin@tenwek.org" data-testid="admin-email-input" />
                </div>
              </div>
              <div className="mt-3">
                <Label htmlFor="admin_pw" className="text-xs">Admin password (min 6 chars)</Label>
                <Input id="admin_pw" type="text" required minLength={6} value={form.admin_password}
                  onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                  placeholder="ChangeMe@2026" data-testid="admin-pw-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => { setCreating(false); resetForm(); }} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="create-org-submit" className="bg-[#1B4332] hover:bg-[#143126]">
                {saving ? "Creating..." : "Create organization"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
